import { Plus, RefreshCw, Users } from 'lucide-react';
import { Fab } from '@/components/ui/Fab';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useEscritorios } from '@/hooks/useEscritorios';
import { Button } from '@/components/ui/button';
import { BarraDaLista } from '@/components/ui/barra-da-lista';
import { Card, CardContent } from '@/components/ui/card';
import { OrigemFilterBar } from '@/components/ui/OrigemFilterBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { BuscaInteligente } from '@/components/ui/smart-search';
import { AbasDaLista } from '@/components/ui/abas-da-lista';
import { useClientSync } from '@/context/ClientSyncProvider';
import { useNotification } from '@/context/NotificationProvider';
import { useAuth } from '@/hooks/useAuth';
import { useDebounce } from '@/hooks/useDebounce';
import { useRigorDaBusca } from '@/hooks/useSmartSearch';

import { fetchClienteCounts, type ClienteCounts } from '@/api/clientes';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { useQuery } from '@tanstack/react-query';
import { ClientSyncProgress } from './ClientSyncProgress';
import { ClienteTable } from './ClienteTable';
import { useAtuacao } from '@/lib/delegacao/atuacao';
import { certificadoVencido } from '@/lib/errors/certificado';

type AbaDeClientes = '' | 'PROSPECTO' | 'ATIVO' | 'INATIVO';

const ABAS: { value: AbaDeClientes; label: string; contagem: keyof ClienteCounts }[] = [
  { value: '', label: 'Todos', contagem: 'total' },
  { value: 'PROSPECTO', label: 'Prospectos', contagem: 'prospecto' },
  { value: 'ATIVO', label: 'Ativos', contagem: 'ativo' },
  { value: 'INATIVO', label: 'Inativos', contagem: 'inativo' },
];

function ClientesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError } = useNotification();
  const { startSync, syncState, checkForActiveJob } = useClientSync();

  // Check for active job when page mounts
  useEffect(() => {
    checkForActiveJob();
  }, [checkForActiveJob]);

  const isSyncing = syncState?.status === 'RUNNING' || syncState?.status === 'PENDING';

  const handleSync = useCallback(async () => {
    if (!user?.has_active_certificate) {
      showError('Cadastre um certificado digital para sincronizar clientes de processos');
      return;
    }
    if (certificadoVencido(user.certificate_valid_until)) {
      showError('Seu certificado digital venceu. Envie o renovado para sincronizar clientes de processos');
      return;
    }
    try {
      await startSync();
    } catch {
      showError('Erro ao iniciar sincronização');
    }
  }, [user, showError, startSync]);

  // Estado da busca
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const searchFilter = debouncedSearch.trim() || undefined;
  // A mesma busca inteligente do Acervo, feita no servidor (backend: repositories/busca_inteligente.py).
  const [rigor, setRigor] = useRigorDaBusca();
  const rigorDaBusca = searchFilter ? rigor : undefined;

  // Filtro de status
  const [statusFilter, setStatusFilter] = useState<AbaDeClientes>('');

  // Filtro de origem (escritório)
  const [origemFilter, setOrigemFilter] = useState<string | undefined>(undefined);

  // Filtro por tags (PLANO_TAGS D7) — no servidor, como os demais desta lista paginada.
  const [filtroTags, setFiltroTags] = useState<string[]>([]);
  const tagIds = filtroTags.length ? filtroTags : undefined;

  // Escritórios do usuário
  const { data: escritorios } = useEscritorios();
  const hasEscritorios = escritorios && escritorios.length > 0;

  // Contagens para as tabs
  const { data: counts } = useQuery<ClienteCounts>({
    queryKey: [...queryKeys.clientes.counts, { search: searchFilter, rigor: rigorDaBusca, origem: origemFilter, tagIds }],
    queryFn: () =>
      fetchClienteCounts({ search: searchFilter, rigor: rigorDaBusca, origem: origemFilter, tag_ids: tagIds }),
  });
  // Quantos a aba ativa mostra (com a busca e a origem aplicadas).
  const contagemDaAba = counts?.[ABAS.find((a) => a.value === statusFilter)?.contagem ?? 'total'];

  // Handlers
  const atuacao = useAtuacao();
  const podeColaborar = !atuacao || atuacao.nivel === 'COLABORACAO';
  const handleNew = useCallback(() => {
    navigate('/app/clientes/novo');
  }, [navigate]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Clientes"
        subtitle="Gerencie seus clientes e prospectos"
        icon={<Users />}
        actions={
          <>
            {/* Importar varre os processos do titular — fica com ele. */}
            {!atuacao && (
              <Button
                variant="outline"
                onClick={handleSync}
                disabled={isSyncing}
                className="hidden sm:inline-flex"
              >
                <RefreshCw className={`mr-1.5 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                Importar de processos
              </Button>
            )}
            {podeColaborar && (
              <Button onClick={handleNew} className="hidden sm:inline-flex">
                <Plus className="mr-1.5 h-4 w-4" />
                Novo Cliente
              </Button>
            )}
          </>
        }
      />

      {podeColaborar && <Fab icon={<Plus className="h-6 w-6" />} label="Novo Cliente" onClick={handleNew} />}

      {/* Progresso da sincronização */}
      <ClientSyncProgress />

      {/* Barra da lista (docs/PLANO_BARRA_DE_LISTA.md): abas à esquerda, a busca no resto. */}
      <BarraDaLista
        abas={
          <AbasDaLista
            nome="Situação"
            value={statusFilter}
            onValueChange={setStatusFilter}
            abas={ABAS.map((a) => ({ value: a.value, label: a.label, contagem: statusFilter === a.value ? contagemDaAba : undefined }))}
          />
        }
        busca={
          <BuscaInteligente
            value={searchQuery}
            onChange={setSearchQuery}
            strictness={rigor}
            onStrictnessChange={setRigor}
            placeholder="Buscar por nome, CPF, CNPJ ou e-mail…"
            contagem={
              searchFilter && contagemDaAba !== undefined
                ? `${contagemDaAba} ${contagemDaAba === 1 ? 'cliente' : 'clientes'}`
                : undefined
            }
          />
        }
        filtros={
          hasEscritorios ? (
            <OrigemFilterBar escritorios={escritorios} value={origemFilter} onChange={setOrigemFilter} includeCompartilhados />
          ) : undefined
        }
        filtrosAtivos={origemFilter ? 1 : 0}
        filtroDeTags={{ value: filtroTags, onChange: setFiltroTags }}
      />

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <ClienteTable
            search={searchFilter}
            rigor={rigor}
            statusFilter={statusFilter || undefined}
            origemFilter={origemFilter}
            tagIds={tagIds}
            onEdit={(c) => navigate(`/app/clientes/${c.id}/editar`)}
          />
        </CardContent>
      </Card>

    </div>
  );
}

export default ClientesPage;
